import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { subscribeUndo, runLastUndo, canUndo } from "@/lib/undo";
import { invalidateResources } from "@/lib/cache";

// Global ⌘Z / Ctrl+Z undo for data changes + a small toast. Mounted once inside
// <Refine> so it shares the query cache. Yields to native text undo while a text
// field is focused, and only intercepts the shortcut when there's something to undo.
const isMac = typeof navigator !== "undefined" && /Mac|iP(hone|ad|od)/.test(navigator.platform || "");
const ZKEY = isMac ? "⌘Z" : "Ctrl+Z";

export function UndoManager() {
  const qc = useQueryClient();
  const [toast, setToast] = useState<{ msg: string; key: number } | null>(null);
  const timer = useRef<any>(null);

  const show = (msg: string) => {
    setToast({ msg, key: Date.now() });
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setToast(null), 3200);
  };

  useEffect(() => {
    return subscribeUndo((e) => {
      if (e.type === "push" && e.hint) show(`${e.label} · ${ZKEY} to undo`);
      else if (e.type === "undo") show(`Undid: ${e.label}`);
      else if (e.type === "empty") show("Nothing to undo");
      else if (e.type === "error") show("Couldn't undo that");
    });
  }, []);

  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      if ((ev.key !== "z" && ev.key !== "Z") || !(ev.metaKey || ev.ctrlKey) || ev.shiftKey || ev.altKey) return;
      const el = document.activeElement as HTMLElement | null;
      const editable = !!el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT" || el.isContentEditable);
      if (editable) return; // let the browser's own text undo run
      if (!canUndo()) return; // don't hijack the shortcut when there's nothing to do
      ev.preventDefault();
      runLastUndo()
        // Only the collection the undo wrote. This used to be a bare
        // `qc.invalidateQueries()`, which threw away and refetched the entire
        // cache on every Cmd+Z -- so every staleTime in the app was silently
        // conditional on a global keystroke (ROADMAP CACHE-01). What is and
        // isn't reached is spelled out in lib/cache.
        .then((done) => (done ? invalidateResources(qc, [done.resource]) : undefined))
        .catch(() => { /* toast already shown via subscribe */ });
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [qc]);

  if (!toast) return null;
  return (
    <div
      key={toast.key}
      role="status"
      aria-live="polite"
      className="fixed bottom-4 left-1/2 z-[100] -translate-x-1/2 rounded-md border border-border bg-card px-3.5 py-2 text-[13px] text-foreground shadow-xl"
    >
      {toast.msg}
    </div>
  );
}
